import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import "../Login/style.css";
import "../ForgotPassword/style.css";
import "./style.css";
import storage from "@utils/storage";
import {
  getOnboardingUrl,
  uploadCreditReport,
  getCreditAssessmentStatus,
  ValidationIssue,
} from "../../API/CreditScore/index";
import { success, failed } from "@components/Common/Toastify";
import { FullPageSpinner } from "@components/Common/Spinner/FullPageSpinner";
import righticon from "../../Assets/Images/right-icon.png";

const MAX_FILE_SIZE_BYTES = 15 * 1024 * 1024;
const SUCCESS_REDIRECT_MS = 3000;

type PageState = "loading" | "upload" | "submitting" | "success" | "validationFailed";

export const CreditScoreAssessment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const storedUser = storage.getUserType();
  const userDataFromState = location.state?.userData;
  const roleTypeFromState = location.state?.roleType;

  const userData = userDataFromState ?? storedUser;
  const roleType = roleTypeFromState ?? storedUser?.userType ?? "borrower";
  const userId = userData?.uuid || userData?.data?.uuid || userData?.userId;

  const [pageState, setPageState] = useState<PageState>("loading");
  const [signupUrl, setSignupUrl] = useState("");
  const [instructions, setInstructions] = useState<string[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState("");
  const [validationErrors, setValidationErrors] = useState<ValidationIssue[]>([]);
  const [validationWarnings, setValidationWarnings] = useState<ValidationIssue[]>([]);
  const [creditScore, setCreditScore] = useState<number | null>(null);
  const [grade, setGrade] = useState("");

  useEffect(() => {
    const loginPage = document.querySelector(".onboarding-page");
    if (loginPage) {
      loginPage.classList.remove("theme-borrower", "theme-lender");
      loginPage.classList.add(`theme-${roleType}`);
    }
  }, [roleType]);

  useEffect(() => {
    if (!userId) {
      failed("User session not found. Please sign up again.");
      navigate("/auth/signup");
      return;
    }

    const init = async () => {
      try {
        const [onboardingRes, statusRes] = await Promise.all([
          getOnboardingUrl(),
          getCreditAssessmentStatus(userId),
        ]);

        setSignupUrl(onboardingRes.data.signupUrl);
        setInstructions(onboardingRes.data.instructions);

        if (statusRes.data.isCreditAssessment) {
          navigate("/auth/login", { state: { roleType, userData } });
          return;
        }

        const latestReport = statusRes.data.latestReport;
        if (
          latestReport &&
          (latestReport.validationStatus === "rejected" ||
            latestReport.validationStatus === "needs_review")
        ) {
          setValidationErrors(latestReport.validationErrors ?? []);
          setValidationWarnings(latestReport.validationWarnings ?? []);
          setPageState("validationFailed");
        } else {
          setPageState("upload");
        }
      } catch (error: unknown) {
        const err = error as { response?: { data?: { message?: string } } };
        failed(
          err?.response?.data?.message ||
            "Failed to load credit assessment. Please try again."
        );
        setPageState("upload");
      }
    };

    init();
  }, [userId, navigate, roleType, userData]);

  useEffect(() => {
    if (pageState !== "success") return;

    const timer = setTimeout(() => {
      navigate("/auth/login", { state: { roleType, userData } });
    }, SUCCESS_REDIRECT_MS);

    return () => clearTimeout(timer);
  }, [pageState, navigate, roleType, userData]);

  const validateFile = (file: File): string | null => {
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      return "Only PDF files are allowed.";
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      return "File size must not exceed 15MB.";
    }
    return null;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setFileError("");
    setValidationErrors([]);
    setValidationWarnings([]);

    if (!file) {
      setSelectedFile(null);
      return;
    }

    const error = validateFile(file);
    if (error) {
      setFileError(error);
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    setSelectedFile(file);
    if (pageState === "validationFailed") {
      setPageState("upload");
    }
  };

  const handleOpenCheckMyFile = () => {
    if (signupUrl) {
      window.open(signupUrl, "_blank", "noopener,noreferrer");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedFile || !userId) {
      setFileError("Please select a credit report PDF to upload.");
      return;
    }

    const error = validateFile(selectedFile);
    if (error) {
      setFileError(error);
      return;
    }

    setPageState("submitting");
    setValidationErrors([]);
    setValidationWarnings([]);

    try {
      const res = await uploadCreditReport(userId, selectedFile);

      const updatedUser = {
        ...userData,
        uuid: userId,
        isCreditAssessment: true,
        creditScore: res.data.creditScore,
      };
      storage.setUserType(updatedUser);

      setCreditScore(res.data.creditScore);
      setGrade(res.data.grade);
      success(res.message || "Credit report processed successfully");
      setPageState("success");
    } catch (error: unknown) {
      const err = error as {
        response?: {
          status?: number;
          data?: {
            message?: string;
            data?: {
              validationErrors?: ValidationIssue[];
              validationWarnings?: ValidationIssue[];
            };
          };
        };
      };

      const status = err?.response?.status;
      const responseData = err?.response?.data;

      if (status === 422) {
        setValidationErrors(responseData?.data?.validationErrors ?? []);
        setValidationWarnings(responseData?.data?.validationWarnings ?? []);
        failed(responseData?.message || "Credit report could not be validated");
        setPageState("validationFailed");
      } else if (status === 404) {
        failed(responseData?.message || "User not found.");
        navigate("/auth/signup");
      } else {
        failed(
          responseData?.message ||
            "Failed to process credit report. Please try again."
        );
        setPageState("upload");
      }
    }
  };

  const renderValidationPanel = () => {
    if (!validationErrors.length && !validationWarnings.length) return null;

    return (
      <div className="validation-panel">
        {validationErrors.length > 0 && (
          <div className="validation-section validation-errors">
            <h5>Validation Errors</h5>
            <ul>
              {validationErrors.map((item, index) => (
                <li key={`err-${index}`}>{item.message}</li>
              ))}
            </ul>
          </div>
        )}
        {validationWarnings.length > 0 && (
          <div className="validation-section validation-warnings">
            <h5>Validation Warnings</h5>
            <ul>
              {validationWarnings.map((item, index) => (
                <li key={`warn-${index}`}>{item.message}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  if (pageState === "loading") {
    return <FullPageSpinner />;
  }

  if (pageState === "success") {
    return (
      <div className="row onboarding-page fg-pw theme-borrower credit-assessment-page">
        <div className="p-0 d-flex flex-column align-items-center my-3">
          <div className="lower-div">
            <div className="credentials-box text-center">
              <div className="success-state">
                <span className="me-2">
                  <img src={righticon} alt="" />
                </span>
                <h4>Credit Assessment Complete</h4>
                <p className="credit-score-result">
                  Your credit score: <strong>{creditScore}</strong>
                  {grade && <span className="grade-badge"> ({grade})</span>}
                </p>
                <p className="redirect-hint">Redirecting to login...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const isSubmitting = pageState === "submitting";

  return (
    <div className={`row onboarding-page fg-pw theme-${roleType || "borrower"} credit-assessment-page`}>
      <div className="p-0 d-flex flex-column align-items-center my-3">
        <div className="login-btn-call align-items-center d-flex justify-content-between">
          <div className="return-home">
            <Link
              to="/auth/login"
              state={{ roleType }}
              className="return text-decoration-none align-ittems-center d-flex"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="23"
                height="23"
                style={{ display: "block", filter: "brightness(0) invert(1)" }}
              >
                <path
                  d="M15.41 7.41 10.83 12l4.58 4.59L14 18l-6-6 6-6z"
                  fill="#000000"
                />
              </svg>
              <span className="ms-2 text-decoration-none">Back</span>
            </Link>
          </div>
        </div>

        <div className="lower-div">
          <div className="credentials-box">
            <div className="heading-para-block">
              <h2 className="text-center">Complete Your Credit Assessment</h2>
              <p className="text-center enter-address">
                Sign up on CheckMyFile, download your credit report, and upload it here.
              </p>
            </div>

            <div className="credit-step">
              <h5 className="step-label">Step 1: Get your credit report</h5>
              <ol className="instructions-list">
                {instructions.map((instruction, index) => (
                  <li key={index}>{instruction}</li>
                ))}
              </ol>
              <button
                type="button"
                className="btn btn-borrower signin w-100 mb-4"
                onClick={handleOpenCheckMyFile}
                disabled={!signupUrl}
              >
                Go to CheckMyFile
              </button>
            </div>

            <div className="credit-step">
              <h5 className="step-label">Step 2: Upload your credit report</h5>

              {renderValidationPanel()}

              <form onSubmit={handleSubmit}>
                <div className="file-upload-area">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,application/pdf"
                    onChange={handleFileChange}
                    disabled={isSubmitting}
                    className="file-input"
                    id="credit-report-input"
                  />
                  <label htmlFor="credit-report-input" className="file-input-label">
                    {selectedFile ? selectedFile.name : "Choose PDF file (max 15MB)"}
                  </label>
                </div>

                {fileError && <p className="file-error">{fileError}</p>}

                {isSubmitting && (
                  <p className="processing-message">
                    Processing report, this may take up to 30 seconds...
                  </p>
                )}

                <button
                  type="submit"
                  className="btn btn-borrower signin w-100 mt-3"
                  disabled={isSubmitting || !selectedFile}
                >
                  {isSubmitting ? "Submitting..." : "Submit Credit Report"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreditScoreAssessment;
